import { RiShareBoxFill, RiGitBranchLine } from "react-icons/ri";

import { formatDate, toCamelCase } from "../utils/units";
import DeploymentLogs from "../components/DeploymentLogs";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { generateImage } from "../redux/image";
import { useWebSocketData } from "../context/WebSocketContext";
import { useParams } from "react-router-dom";
import { fetchWrapper } from "../utils/fetchWrapper";
import { clear, updateSettings } from "../redux/setting";
import toast from "react-hot-toast";
import { MdPlayArrow } from "react-icons/md";
import KafkaLogs from "../components/KafkaLogs";
import BootstrapModal from "../components/ui/BootstrapModal";
import RenderFields from "../components/ui/RenderFields";
import PopupPreview from "../components/PopupPreview";
import { Link, useNavigate } from "react-router-dom";
import "./ProjectDetails.css";

// Sub-components
import ConfirmModal from "../components/ui/ConfirmModal";
import ProjectInfoCard from "../components/project/ProjectInfoCard";
import SSLManagementCard from "../components/project/SSLManagementCard";
import EmailNotificationCard from "../components/project/EmailNotificationCard";
import PopupConfigCard from "../components/project/PopupConfigCard";

const payloadDefault = {
  logo: {
    url: "logo.png",
    alt: "logo",
    height: "50px",
    width: "auto",
  },
  banner: {
    url: "",
    alt: "",
  },
  download: {
    url: "",
    alt: "",
  },
  dark: false,
  title: "",
  area: "",
  returns: "/thankyou.html",
  phone: "",
  phone2: "",
  whatsAppMessage: "",
  whatsIcon: true,
  phoneIcon: true,
  offerTag: true,
  crmId: "",
  crmDes: "",
  offerTagName: "<span>EXCITING</span><span> OFFERS!!",
  interest: "",
  notificationEmails: [],
  gtmCodes: [],
  fbPixelCodes: [],
  metaTags: {
    basic: {
      title: "",
      description: "",
      keywords: "",
      author: "",
    },
    openGraph: {
      title: "",
      description: "",
      image: "",
      url: "",
      type: "website",
    },
    twitter: {
      card: "summary_large_image",
      title: "",
      description: "",
      image: "",
    },
    additional: {
      viewport: "width=device-width, initial-scale=1.0",
      robots: "index, follow",
      canonical: "",
    },
  },
};

const sanitizeDomain = (domain) => {
  return domain.toLowerCase().replace(/[^a-z0-9-]/g, "-");
};

const ProjectDetails = () => {
  const { projectid } = useParams();
  const [project, setProject] = useState({});

  // Activate / deactivate the project (status only — never triggers a re-deploy)
  const [statusPopup, setStatusPopup] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const { error, success, loading } = useSelector((state) => state.setting);
  const [form, setForm] = useState(payloadDefault);
  const dispatch = useDispatch();

  // SSL Certificate state
  const [sslStatus, setSslStatus] = useState(null);
  const [sslLoading, setSslLoading] = useState(false);
  const [sslError, setSslError] = useState(null);
  const [sslPopup, setSslPopup] = useState(null);

  const [folders, setFolders] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState(null);

  // Email management state
  const [notificationEmails, setNotificationEmails] = useState([]);
  const [newEmail, setNewEmail] = useState("");
  const [emailLoading, setEmailLoading] = useState(false);

  useEffect(() => {
    if (
      project?.setting &&
      Object.entries(project.setting).length > 0 &&
      selectedFolder !== null
    ) {
      // Convert main project selection (sanitized domain) to empty string for lookup
      const folderKey =
        selectedFolder === sanitizeDomain(project.domain) ? "" : selectedFolder;

      if (project.setting[folderKey]) {
        setForm(project.setting[folderKey]);
      } else if (
        folderKey === "" &&
        project.setting[sanitizeDomain(project.domain)]
      ) {
        setForm(project.setting[sanitizeDomain(project.domain)]);
      } else {
        setForm(payloadDefault);
      }
    } else if (selectedFolder !== null) {
      // No settings exist yet, use defaults
      setForm(payloadDefault);
    }
  }, [project, selectedFolder]);

  useEffect(() => {
    if (error) {
      toast.error(error);
      dispatch(clear());
    }
    if (success) {
      toast.success(success);
      dispatch(clear());
      loadProjects();
    }
  }, [error, success, dispatch]);

  const nextStatus = project.status === "active" ? "inactive" : "active";

  const updateProjectStatus = async () => {
    setStatusLoading(true);
    try {
      const res = await fetchWrapper.put(`/projects/${projectid}/status`, {
        status: nextStatus,
      });
      setProject((prev) => ({
        ...prev,
        status: res.data?.status || nextStatus,
      }));
      toast.success(
        nextStatus === "active"
          ? "Project activated successfully"
          : "Project deactivated successfully",
      );
      setStatusPopup(false);
    } catch (err) {
      console.error("Failed to update project status", err);
      toast.error(
        err.response?.data?.message || "Failed to update project status",
      );
    } finally {
      setStatusLoading(false);
    }
  };

  const loadProjects = async () => {
    try {
      const res = await fetchWrapper.get(`/projects/${projectid}`);
      const projectData = res.data?.[0];
      if (!projectData) {
        setLoadError("Project not found.");
        return;
      }
      setProject(projectData);
      setNotificationEmails(projectData.notificationEmails || []);
    } catch (err) {
      console.error("Failed to fetch projects", err);
      setLoadError(err?.response?.data?.message || "Failed to load project.");
    }
  };

  const loadFolders = async () => {
    if (!project?.domain) return;
    try {
      const res = await fetchWrapper.get(`/setting/folders/${project.domain}`);
      setFolders(res.data?.folders || []);
    } catch (err) {
      console.error("Failed to fetch folders", err);
    }
  };

  useEffect(() => {
    if (project?.domain) {
      loadFolders();
    }
  }, [project?.domain]);

  // Set default selection after folders are loaded
  useEffect(() => {
    if (project?.domain && !selectedFolder) {
      const mainProjectFolder = sanitizeDomain(project.domain);
      if (folders.length > 0) {
        // Check if main project exists in folders list
        if (folders.includes(mainProjectFolder)) {
          setSelectedFolder(mainProjectFolder);
        } else {
          // Select the first available folder
          setSelectedFolder(folders[0]);
        }
      } else {
        // No sub-folders exist; default to the main project domain
        setSelectedFolder(mainProjectFolder);
      }
    }
  }, [folders, project?.domain]);

  const projectUrl = useMemo(
    () =>
      project?.domain?.startsWith("http")
        ? project.domain
        : project?.domain
          ? `https://${project.domain}`
          : "",
    [project.domain],
  );

  const { images, loading: imgLoading } = useSelector((state) => state.image);
  const cachedImage = useMemo(
    () => images[project.domain] || null,
    [images, project.domain],
  );
  const { live } = useWebSocketData();
  const { permissions } = useSelector((state) => state.auth);
  const isViewOnly = permissions?.ProjectsViewOnly === true;
  const navigate = useNavigate();

  useEffect(() => {
    if (isViewOnly) {
      toast.error("You do not have permission to view project details.");
      navigate("/projects");
    }
  }, [isViewOnly, navigate]);

  useEffect(() => {
    if (projectid && !isViewOnly) {
      loadProjects();
    }
  }, [projectid, isViewOnly]);

  const status = useMemo(() => {
    const filtered = live.filter(
      (e) => e.event === "project_status_check" && e.domain === project.domain,
    );

    if (filtered.length === 0) return null;

    // Sort descending by timestamp
    filtered.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

    return filtered[0]; // latest status object
  }, [live, project.domain]);

  useEffect(() => {
    if (!project?.domain) return;
    // If we don't have a cached image and we're not already loading one, request it
    dispatch(generateImage(project.domain));
  }, [project.domain, dispatch]);

  // Check SSL status when project loads
  useEffect(() => {
    if (project?.domain) {
      checkSSLStatus();
    }
  }, [project?.domain]);

  const submitForm = (updatedForm = null) => {
    // Use provided updatedForm or fallback to current state
    const currentForm = updatedForm || form;
    
    // Convert main project selection to empty string for backend
    const folderNameToSend =
      selectedFolder === sanitizeDomain(project.domain) ? "" : selectedFolder;
    dispatch(
      updateSettings({
        payload: currentForm,
        projectId: projectid,
        folderName: folderNameToSend,
      }),
    );
  };

  // Email management functions
  const addNotificationEmail = async () => {
    if (!newEmail || !newEmail.includes("@")) {
      toast.error("Please enter a valid email address");
      return;
    }

    if (notificationEmails.includes(newEmail.toLowerCase())) {
      toast.error("Email already added");
      return;
    }

    setEmailLoading(true);
    try {
      const updatedEmails = [...notificationEmails, newEmail.toLowerCase()];
      await fetchWrapper.put(`/projects/${projectid}`, {
        notificationEmails: updatedEmails,
      });
      setNotificationEmails(updatedEmails);
      setNewEmail("");
      toast.success("Email added successfully");
    } catch (err) {
      toast.error("Failed to add email");
      console.error(err);
    } finally {
      setEmailLoading(false);
    }
  };

  const removeNotificationEmail = async (emailToRemove) => {
    setEmailLoading(true);
    try {
      const updatedEmails = notificationEmails.filter(
        (e) => e !== emailToRemove,
      );
      await fetchWrapper.put(`/projects/${projectid}`, {
        notificationEmails: updatedEmails,
      });
      setNotificationEmails(updatedEmails);
      toast.success("Email removed successfully");
    } catch (err) {
      toast.error("Failed to remove email");
      console.error(err);
    } finally {
      setEmailLoading(false);
    }
  };

  // Email management functions
  const addSubProjectNotificationEmail = async () => {
    if (!newEmail || !newEmail.includes("@")) {
      toast.error("Please enter a valid email address");
      return;
    }

    if (form?.notificationEmails?.includes(newEmail.toLowerCase())) {
      toast.error("Email already added");
      return;
    }
    const updatedForm = {
      ...form,
      notificationEmails: [...form.notificationEmails, newEmail.toLowerCase()],
    };
    setForm(updatedForm);
    submitForm(updatedForm);
    setNewEmail(""); // Reset field
  };

  const removeSubProjectNotificationEmail = async (emailToRemove) => {
    const updatedForm = {
      ...form,
      notificationEmails: form.notificationEmails.filter(
        (e) => e !== emailToRemove,
      ),
    };
    setForm(updatedForm);
    submitForm(updatedForm);
  };

  // SSL Certificate Management Functions
  const checkSSLStatus = async () => {
    if (!project?.domain) return;

    setSslLoading(true);
    setSslError(null);

    try {
      const res = await fetchWrapper.get(`/ssl/status/${project.domain}`);
      setSslStatus(res.data);
    } catch (err) {
      console.error("Failed to check SSL status", err);
      setSslError(err.response?.data?.message || "Failed to check SSL status");
      toast.error("Failed to check SSL status");
    } finally {
      setSslLoading(false);
    }
  };

  const installSSL = async () => {
    if (!project?.domain) return;

    setSslLoading(true);
    setSslError(null);
    setSslPopup(null);

    try {
      const res = await fetchWrapper.post("/ssl/install", {
        domain: project.domain,
        email: process.env.REACT_APP_SSL_EMAIL || "web.developer@kaycomm.in",
      });

      toast.success(
        res.data.message || "SSL certificate installed successfully",
      );

      // Refresh SSL status
      setTimeout(() => checkSSLStatus(), 2000);
    } catch (err) {
      console.error("Failed to install SSL", err);
      const errorMsg =
        err.response?.data?.message || "Failed to install SSL certificate";
      setSslError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setSslLoading(false);
    }
  };

  const uninstallSSL = async () => {
    if (!project?.domain) return;

    setSslLoading(true);
    setSslError(null);
    setSslPopup(null);

    try {
      const res = await fetchWrapper.delete(`/ssl/uninstall/${project.domain}`);

      toast.success(res.data.message || "SSL certificate removed successfully");

      // Refresh SSL status
      setTimeout(() => checkSSLStatus(), 2000);
    } catch (err) {
      console.error("Failed to uninstall SSL", err);
      const errorMsg =
        err.response?.data?.message || "Failed to remove SSL certificate";
      setSslError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setSslLoading(false);
    }
  };

  const renewSSL = async () => {
    if (!project?.domain) return;

    setSslLoading(true);
    setSslError(null);
    setSslPopup(null);
    try {
      const res = await fetchWrapper.post("/ssl/renew", {
        domain: project.domain,
      });

      toast.success(res.data.message || "SSL certificate renewed successfully");

      // Refresh SSL status
      setTimeout(() => checkSSLStatus(), 2000);
    } catch (err) {
      console.error("Failed to renew SSL", err);
      const errorMsg =
        err.response?.data?.message || "Failed to renew SSL certificate";
      setSslError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setSslLoading(false);
    }
  };

  if (project && project._id) {
    return (
      <div className="project-details-container">
        <div className="project-details-header">
          <div>
            <Link to="/projects" className="back-link">
              <i className="bi bi-arrow-left"></i> Back to Projects
            </Link>
            <h3 className="project-details-title">
              {project.projectName}
              <span
                className={`status-badge-pill ${project.status === "active" ? "live" : "offline"}`}
              >
                <span
                  className={`status-dot ${project.status === "active" ? "live" : "offline"}`}
                ></span>
                {toCamelCase(project.status)}
              </span>
            </h3>
          </div>
        </div>

        {/* Project Info Card */}
        <ProjectInfoCard
          project={project}
          projectUrl={projectUrl}
          imgLoading={imgLoading}
          cachedImage={cachedImage}
          status={status}
          isViewOnly={isViewOnly}
          onToggleStatus={() => setStatusPopup(true)}
          statusLoading={statusLoading}
        />

        {/* SSL Certificate Section */}
        <SSLManagementCard
          project={project}
          sslStatus={sslStatus}
          sslLoading={sslLoading}
          sslError={sslError}
          setSslPopup={setSslPopup}
          checkSSLStatus={checkSSLStatus}
        />

        {/* Email Notification Section */}
        <EmailNotificationCard
          notificationEmails={notificationEmails}
          newEmail={newEmail}
          setNewEmail={setNewEmail}
          emailLoading={emailLoading}
          addNotificationEmail={addNotificationEmail}
          removeNotificationEmail={removeNotificationEmail}
        />

        {/* Popup Configuration Section */}
        <PopupConfigCard
          project={project}
          folders={folders}
          selectedFolder={selectedFolder}
          setSelectedFolder={setSelectedFolder}
          sanitizeDomain={sanitizeDomain}
          form={form}
          setForm={setForm}
          payloadDefault={payloadDefault}
          submitForm={submitForm}
          newEmail={newEmail}
          setNewEmail={setNewEmail}
          addSubProjectNotificationEmail={addSubProjectNotificationEmail}
          removeSubProjectNotificationEmail={removeSubProjectNotificationEmail}
          loading={loading}
        />

        <BootstrapModal
          id="Modal"
          title={
            sslPopup === "remove"
              ? "Remove SSL Certificate"
              : sslPopup === "install"
                ? "Install SSL Certificate"
                : "Renew SSL Certificate"
          }
          show={sslPopup}
          buttonText={
            sslPopup === "remove"
              ? "Remove"
              : sslPopup === "install"
                ? "Install"
                : "Renew"
          }
          buttonColor={
            sslPopup === "remove"
              ? "danger"
              : sslPopup === "install"
                ? "success"
                : "warning"
          }
          onClose={() => setSslPopup(null)}
          onSave={() => {
            if (sslPopup === "remove") uninstallSSL();
            else if (sslPopup === "install") installSSL();
            else renewSSL();
          }}
        >
          <p>
            Are you sure you want to{" "}
            {sslPopup === "remove"
              ? "remove"
              : sslPopup === "install"
                ? "install"
                : "renew"}{" "}
            SSL certificate for <strong>{project?.projectName}</strong> (
            {project?.domain})?
          </p>
          {sslPopup === "remove" && (
            <p className="text-danger small mb-0">
              This action cannot be undone.
            </p>
          )}
        </BootstrapModal>

        <ConfirmModal
          show={statusPopup}
          variant={nextStatus === "active" ? "info" : "danger"}
          title={
            nextStatus === "active"
              ? "Activate this project?"
              : "Deactivate this project?"
          }
          message={
            nextStatus === "active"
              ? "The project will be marked active across the dashboard."
              : "The project will be marked inactive across the dashboard. The site itself stays online."
          }
          detail={`${project.projectName} (${project.domain})`}
          confirmPhrase={project.projectName}
          confirmText={nextStatus === "active" ? "Activate" : "Deactivate"}
          loading={statusLoading}
          onConfirm={updateProjectStatus}
          onCancel={() => setStatusPopup(false)}
        />
      </div>
    );
  } else if (loadError) {
    return (
      <div className="d-flex flex-column align-items-center justify-content-center w-100 vh-100 gap-3">
        <p className="text-danger">{loadError}</p>
        <Link to="/projects" className="btn btn-secondary">Back to Projects</Link>
      </div>
    );
  } else {
    return (
      <div className="d-flex align-items-center justify-content-center w-100 vh-100">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }
};

export default ProjectDetails;
